import { useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import API_CONFIG from "../config/api";
import useBusinessResource from "../hooks/useBusinessResource";
import { BusinessScope, invalidateBusinessResources } from "../utils/businessResourceFreshness";

export interface PaymentOrderOption {
  id: number;
  invoice_number: string;
  order_date: string;
  total: number;
  paid_amount: number;
  due_amount: number;
  payment_status: string;
  current_payment_amount: number;
  user_id: number;
  selected?: boolean;
  payment_amount?: number;
  unavailable?: boolean;
}
export interface OrderAllocation { order_id: number; amount: number; }
interface Options extends BusinessScope {
  paymentType: "in" | "out";
  entityId?: number | string | null;
  orderId?: number | string | null;
  editingPaymentId?: number | string | null;
  active: boolean;
  enabled?: boolean;
}
const EMPTY: PaymentOrderOption[] = [];

export function assertFreshAllocations(orders: PaymentOrderOption[], links: OrderAllocation[]) {
  for (const link of links) {
    const order = orders.find((row) => Number(row.id) === Number(link.order_id));
    if (!order || !Number.isFinite(Number(order.due_amount)) || !Number.isFinite(Number(link.amount)) || Number(link.amount) <= 0 ||
      Number(link.amount) > Number(order.due_amount) + 0.0001) {
      throw new Error("Le solde d'une commande a changé. Vérifiez les montants affectés ; votre saisie est conservée.");
    }
  }
}

export function mergePaymentOrders<T extends Omit<PaymentOrderOption, "current_payment_amount" | "user_id">>(
  fresh: PaymentOrderOption[], previous: T[]
): PaymentOrderOption[] {
  const old = new Map(previous.map((row) => [Number(row.id), row]));
  const ids = new Set(fresh.map((row) => Number(row.id)));
  return [
    ...fresh.map((row) => ({ ...row, selected: old.get(Number(row.id))?.selected || false,
      payment_amount: old.get(Number(row.id))?.payment_amount ?? 0, unavailable: false })),
    ...previous.filter((row) => row.selected && !ids.has(Number(row.id))).map((row) => ({
      ...row, current_payment_amount: 0, user_id: 0, due_amount: 0, unavailable: true,
    })),
  ];
}

export default function usePaymentOrderOptions({ paymentType, entityId, orderId,
  editingPaymentId, active, enabled = true, ...scope }: Options) {
  const client = useQueryClient();
  const resource = useBusinessResource<{ orders: PaymentOrderOption[] }>({
    ...scope, resource: "payment-order-options", parameters: [paymentType,
      String(entityId || ""), String(orderId || ""), String(editingPaymentId || "")],
    enabled: Boolean(enabled && active && entityId), active,
    load: async (signal) => {
      const response = await apiClient.get(API_CONFIG.apiUrl("/payments/order-options"), {
        signal, headers: { "X-Company-Id": scope.companyId },
        params: { company_id: scope.companyId, warehouse_id: scope.warehouseId,
          payment_type: paymentType, user_id: entityId,
          ...(orderId ? { order_id: orderId } : {}),
          ...(editingPaymentId ? { editing_payment_id: editingPaymentId } : {}),
        },
      });
      if (!Array.isArray(response.data?.orders)) throw new Error("Réponse des commandes invalide.");
      return response.data;
    },
  });
  return {
    ...resource, orders: resource.data?.orders || EMPTY,
    verify: async (links: OrderAllocation[]) => {
      const fresh = await resource.refresh({ force: true });
      assertFreshAllocations(fresh.orders, links);
      return fresh.orders;
    },
    invalidate: () => invalidateBusinessResources(client, scope, "payment"),
  };
}
